import React, { useState } from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    Box,
    Typography,
    Button,
    Chip,
    Alert,
    CircularProgress,
    Grid2,
    Divider
} from '@mui/material';
import {
    AccountBalanceWallet as WalletIcon,
    TrendingUp as TrendingUpIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Build as BuildIcon
} from '@mui/icons-material';
import { FeeMetrics } from '../../types/DashboardTypes';
import { useSmartTemplates } from '../../hooks/useSmartTemplates';

interface FeeCollectionCardProps {
    metrics: FeeMetrics;
    dexType: 'uniswap' | 'aerodrome';
    contractAddress?: string;
}

const FeeCollectionCard: React.FC<FeeCollectionCardProps> = ({
    metrics,
    dexType,
    contractAddress
}) => {
    const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);

    // Initialize smart templates hook with navigation callback
    const smartTemplates = useSmartTemplates((path: string) => {
        // For now, just log the navigation - this can be enhanced with actual routing
        console.log('Navigate to:', path);
        // In a real implementation, this would trigger navigation to the transaction builder
        alert(`Navigation to ${path} - Template created and ready for transaction builder!`);
    });

    // Show meaningful message when no data is available
    if (!metrics) {
        return (
            <Card elevation={3}>
                <CardHeader
                    title={
                        <Box display="flex" alignItems="center" gap={1}>
                            <WalletIcon color="disabled" />
                            <Typography variant="h6" fontWeight="bold" color="textSecondary">
                                Fee Collection Status
                            </Typography>
                        </Box>
                    }
                    subheader="No connection to contract data"
                />
                <CardContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            📡 No Data Connection
                        </Typography>
                        <Typography variant="body2">
                            Unable to connect to {dexType === 'uniswap' ? 'Uniswap V3' : 'Aerodrome V2'} contract.
                            This could indicate:
                        </Typography>
                        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                            <li>Contract not deployed or incorrect address</li>
                            <li>Network connection issues</li>
                            <li>No liquidity positions available</li>
                            <li>RPC provider issues</li>
                        </Box>
                    </Alert>

                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                        Check your wallet connection and try refreshing the page
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const handleCreateCollectFeesTransaction = async () => {
        if (!contractAddress) {
            console.error('Contract address not provided');
            return;
        }

        setIsCreatingTemplate(true);
        try {
            // Create smart template for collecting all fees
            await smartTemplates.collectAllFeesWorkflow(
                contractAddress,
                {
                    unclaimedFeesETH: metrics.unclaimedFeesETH,
                    estimatedGasCost: metrics.estimatedGasCost,
                    profitAfterGas: metrics.profitAfterGas,
                    isCollectionViable: metrics.isCollectionViable
                },
                '180000' // Estimated gas for collectAllFees
            );
        } catch (error) {
            console.error('Failed to create collect fees template:', error);
        } finally {
            setIsCreatingTemplate(false);
        }
    };

    const handleCreateReinvestTransaction = async () => {
        if (!contractAddress) {
            console.error('Contract address not provided');
            return;
        }

        setIsCreatingTemplate(true);
        try {
            // For Uniswap, we'd need a tokenId - using "1" as example
            // For Aerodrome, we'd need pool address - using contract address as example
            if (dexType === 'uniswap') {
                await smartTemplates.collectAndReinvestWorkflow(
                    contractAddress,
                    '1', // Example tokenId - this should come from position data
                    '0', // minToken0 - calculated based on slippage tolerance
                    '0', // minToken1 - calculated based on slippage tolerance
                    '250000' // Estimated gas for collectAndReinvestFees
                );
            } else {
                // Aerodrome workflow would be different
                console.log('Aerodrome reinvest workflow - template creation needed');
            }
        } catch (error) {
            console.error('Failed to create reinvest template:', error);
        } finally {
            setIsCreatingTemplate(false);
        }
    };

    const formatETH = (value: string) => {
        const num = parseFloat(value);
        if (num === 0) return '0.000000';
        if (num < 0.000001) return num.toExponential(3); // Show very small numbers in scientific notation
        return num.toFixed(6);
    };

    const formatMEMER = (value: string) => {
        const num = parseFloat(value);
        if (num === 0) return '0';
        if (num < 0.01) return num.toFixed(6); // Show more decimals for small amounts
        return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    const isDustAmount = (ethValue: string, memerValue: string) => {
        const ethNum = parseFloat(ethValue);
        const memerNum = parseFloat(memerValue);
        return ethNum > 0 && ethNum < 0.001; // Less than 0.001 ETH is considered dust
    };

    const getViabilityColor = () => {
        if (metrics.isCollectionViable) {
            return 'success';
        }
        return 'warning';
    };

    const getViabilityIcon = () => {
        if (metrics.isCollectionViable) {
            return <CheckCircleIcon fontSize="small" />;
        }
        return <WarningIcon fontSize="small" />;
    };

    return (
        <Card elevation={3}>
            <CardHeader
                title={
                    <Box display="flex" alignItems="center" gap={1}>
                        <WalletIcon color="primary" />
                        <Typography variant="h6" fontWeight="bold">
                            Fee Collection Status
                        </Typography>
                    </Box>
                }
                subheader={`Last updated: ${new Date(metrics.lastUpdated).toLocaleTimeString()}`}
            />
            <CardContent>
                <Grid2 container spacing={3}>
                    {/* Fee Metrics Display */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h4" fontWeight="bold" color="primary">
                                {formatETH(metrics.totalValueETH)} ETH
                            </Typography>
                            <Typography variant="subtitle1" color="textSecondary">
                                Total Unclaimed Fees
                            </Typography>
                        </Box>

                        <Box display="flex" gap={1} mb={2}>
                            <Chip
                                label={`${formatETH(metrics.unclaimedFeesETH)} WETH`}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                            <Chip
                                label={`${formatMEMER(metrics.unclaimedFeesMEMER)} MEMER`}
                                color="secondary"
                                variant="outlined"
                                size="small"
                            />
                        </Box>

                        {/* Dust Amount Alert */}
                        {isDustAmount(metrics.totalValueETH, metrics.unclaimedFeesMEMER) && (
                            <Alert severity="info" sx={{ my: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    🧹 Dust Amounts Detected
                                </Typography>
                                <Typography variant="body2">
                                    These small fees likely accumulated during pool creation or initial setup.
                                    They're real but too small to be economically viable to collect.
                                </Typography>
                            </Alert>
                        )}

                        <Divider sx={{ my: 2 }} />

                        {/* Economic Analysis */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                Economic Analysis
                            </Typography>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2" color="textSecondary">
                                    Estimated Gas Cost:
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatETH(metrics.estimatedGasCost)} ETH
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2" color="textSecondary">
                                    Profit After Gas:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                    color={parseFloat(metrics.profitAfterGas) > 0 ? 'success.main' : 'error.main'}
                                >
                                    {formatETH(metrics.profitAfterGas)} ETH
                                </Typography>
                            </Box>
                        </Box>
                    </Grid2>

                    {/* Viability Status & Actions */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Alert
                            severity={getViabilityColor()}
                            icon={getViabilityIcon()}
                            sx={{ mb: 3 }}
                        >
                            {metrics.isCollectionViable ? (
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        ✅ Profitable to Collect
                                    </Typography>
                                    <Typography variant="body2">
                                        Profit after gas: {formatETH(metrics.profitAfterGas)} ETH
                                    </Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        ⏳ Wait for More Fees
                                    </Typography>
                                    <Typography variant="body2">
                                        Need {formatETH((parseFloat(metrics.estimatedGasCost) * 3 - parseFloat(metrics.totalValueETH)).toString())} ETH more for 3x gas coverage
                                    </Typography>
                                </Box>
                            )}
                        </Alert>

                        {/* Action Buttons */}
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Button
                                variant="outlined"
                                onClick={handleCreateCollectFeesTransaction}
                                disabled={!metrics.isCollectionViable || isCreatingTemplate}
                                startIcon={isCreatingTemplate ? <CircularProgress size={20} /> : <BuildIcon />}
                                fullWidth
                            >
                                {isCreatingTemplate ? 'Creating Transaction...' : 'Create Collect Fees Transaction'}
                            </Button>

                            <Button
                                variant="contained"
                                onClick={handleCreateReinvestTransaction}
                                disabled={!metrics.isCollectionViable || isCreatingTemplate}
                                startIcon={isCreatingTemplate ? <CircularProgress size={20} /> : <BuildIcon />}
                                color="success"
                                fullWidth
                            >
                                {isCreatingTemplate ? 'Creating Transaction...' : 'Create Collect & Reinvest Transaction'}
                            </Button>
                        </Box>

                        <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                            💡 Tip: "Collect & Reinvest" compounds your fees back into the {dexType === 'uniswap' ? 'position' : 'pool'} for exponential growth
                        </Typography>
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    );
};

export default FeeCollectionCard;
